import { useState, useEffect, useRef, useCallback } from 'react';
import { rankingsApi } from '../services/api';

// Lazy-load socket.io-client
let socketModule = null;
const getSocket = async () => {
  if (!socketModule) {
    socketModule = await import('socket.io-client');
  }
  return socketModule.default || socketModule.io;
};

export function useLiveRanking(territoryId, edition) {
  const [rankings, setRankings] = useState([]);
  const [liveTeams, setLiveTeams] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [raceActive, setRaceActive] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  // Fetch initial rankings
  const fetchRankings = useCallback(async () => {
    if (!territoryId) return;
    try {
      const [all, live] = await Promise.all([
        rankingsApi.list(territoryId, edition ? { edition } : {}),
        rankingsApi.live(territoryId, edition)
      ]);
      setRankings(all);
      setLiveTeams(live);
      setRaceActive(live.length > 0);
    } catch (err) {
      console.error('Erreur chargement classement:', err);
    } finally {
      setLoading(false);
    }
  }, [territoryId, edition]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  // Socket.IO connection
  useEffect(() => {
    if (!territoryId) return;

    let socket;
    const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://radar-lejeu.fr';

    // ── FIX: read the JWT token from localStorage and pass it to socket ──────
    const token = localStorage.getItem('token');
    if (!token) return; // don't connect if user is not logged in

    getSocket().then(io => {
      socket = io(API_URL, {
        transports: ['websocket', 'polling'],
        auth: { token }, // ← sends token to the backend io.use() middleware
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        setIsConnected(true);
        socket.emit('join:territory', territoryId);
      });

      socket.on('connect_error', (err) => {
        // Fires when backend rejects the token (Unauthorized)
        console.warn('Socket connection refused:', err.message);
        setIsConnected(false);
      });

      socket.on('disconnect', () => setIsConnected(false));

      // Full leaderboard refresh trigger
      socket.on('ranking:updated', ({ territoryId: tid }) => {
        if (tid === territoryId) {
          fetchRankings();
          setLastUpdate(new Date());
        }
      });

      // Live split recorded — update specific team in liveTeams
      socket.on('split:recorded', ({ teamId, checkpointName, checkpointOrder, timeMs }) => {
        setLiveTeams(prev => prev.map(t =>
          t._id === teamId
            ? { ...t, lastCheckpoint: checkpointName, lastCheckpointOrder: checkpointOrder, lastSplitMs: timeMs }
            : t
        ));
        setLastUpdate(new Date());
      });

      // A team has finished
      socket.on('team:finished', ({ teamId, totalTimeDisplay, totalPoints }) => {
        setLiveTeams(prev => prev.filter(t => t._id !== teamId));
        setRankings(prev => {
          const exists = prev.find(t => t._id === teamId);
          if (exists) return prev.map(t => t._id === teamId ? { ...t, totalTimeDisplay, totalPoints, isLive: false } : t);
          return prev;
        });
        setLastUpdate(new Date());
        // Full refresh after short delay to get updated ranks
        setTimeout(fetchRankings, 1000);
      });

      socket.on('race:started', ({ territoryId: tid }) => {
        if (tid === territoryId) { setRaceActive(true); fetchRankings(); }
      });

      socket.on('race:stopped', ({ territoryId: tid }) => {
        if (tid === territoryId) { setRaceActive(false); fetchRankings(); }
      });
    });

    return () => {
      if (socket) {
        socket.emit('leave:territory', territoryId);
        socket.disconnect();
      }
    };
  }, [territoryId, fetchRankings]);

  const refresh = useCallback(() => fetchRankings(), [fetchRankings]);

  return {
    rankings,
    liveTeams,
    isConnected,
    raceActive,
    lastUpdate,
    loading,
    refresh
  };
}